import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Typography,
} from "@mui/material";
import {
  Message as MessageIcon,
  Add as AddIcon,
  Inbox as InboxIcon,
  MonitorHeart as MonitorHeartIcon,
  Public as PublicIcon,
} from "@mui/icons-material";

import Page from "./Page";

const Communication = () => {
  const navigate = useNavigate();

  const menuItems = [
    {
      title: "Messages",
      description: "View all messages with reply tracking and management",
      icon: <MessageIcon sx={{ fontSize: 60, color: "primary.main" }} />,
      path: "/messages",
      color: "#1976d2",
    },
    {
      title: "Create Message",
      description: "Send a new message to users with targeting filters",
      icon: <AddIcon sx={{ fontSize: 60, color: "success.main" }} />,
      path: "/createmessage",
      color: "#2e7d32",
    },
    {
      title: "Replies Inbox",
      description: "View and manage user replies to your messages",
      icon: <InboxIcon sx={{ fontSize: 60, color: "warning.main" }} />,
      path: "/messageinbox",
      color: "#ed6c02",
    },
    {
      title: "Work Queue Monitor",
      description:
        "Monitor active background jobs, queue backlog, and durations",
      icon: <MonitorHeartIcon sx={{ fontSize: 60, color: "secondary.main" }} />,
      path: "/workqueue-monitor",
      color: "#7b1fa2",
    },
    {
      title: "Public Links Admin",
      description: "Manage categories and links shown on the public links page",
      icon: <PublicIcon sx={{ fontSize: 60, color: "info.main" }} />,
      path: "/links-admin",
      color: "#0288d1",
    },
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Page pageName="Communication">
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {menuItems.map((item, index) => (
          <Box
            key={index}
            sx={{ flex: 1, minWidth: 250 }}
          >
            <Card
              sx={{
                height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleNavigate(item.path)}
                sx={{ height: "100%", p: 2 }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        backgroundColor: `${item.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{ fontWeight: "bold" }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 40 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 4, p: 3, bgcolor: "grey.50", borderRadius: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Chip
                label="User Targeting"
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label="Reply Tracking"
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              Send targeted messages to specific users or groups based on
              client, project, province, banner, or retailer filters.
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Chip
                label="Bilingual Support"
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label="Priority Levels"
                size="small"
                color="warning"
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              Create messages in both English and French with priority levels
              (normal, high, urgent) and optional acknowledgement requirements.
            </Box>
          </Box>
        </Box>
      </Box>
    </Page>
  );
};

export default Communication;
